<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { CategoryConversationMode } from '../types/category.types'

const props = defineProps<{
  linkError?: string | null
  initialMode?: CategoryConversationMode
}>()

const mode = defineModel<CategoryConversationMode>('mode', { required: true })
const whatsappLink = defineModel<string>('whatsappLink', { required: true })

const { t } = useI18n()

const options: { value: CategoryConversationMode; title: string; desc: string }[] = [
  { value: 'app', title: 'categories.conversationModeApp', desc: 'categories.conversationModeAppDesc' },
  { value: 'whatsapp', title: 'categories.conversationModeWhatsapp', desc: 'categories.conversationModeWhatsappDesc' },
  { value: 'none', title: 'categories.conversationModeNone', desc: 'categories.conversationModeNoneDesc' }
]
</script>

<template>
  <fieldset class="border-t-2 border-gray-200 pt-4">
    <legend class="float-left w-full text-sm font-medium text-gray-700 mb-2">
      {{ t('categories.conversationMode') }}
    </legend>

    <div class="clear-both grid grid-cols-1 gap-2">
      <div
        v-for="option in options"
        :key="option.value"
        :class="[
          'rounded border-2 transition-colors',
          mode === option.value ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        ]"
      >
        <label class="flex items-start gap-3 p-3 cursor-pointer">
          <input
            v-model="mode"
            type="radio"
            name="conversationMode"
            :value="option.value"
            class="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
          >
          <span class="flex-1 min-w-0">
            <span class="block text-sm font-medium text-gray-900">{{ t(option.title) }}</span>
            <span class="block text-xs text-gray-500 mt-0.5">{{ t(option.desc) }}</span>
          </span>
        </label>

        <div v-if="option.value === 'whatsapp' && mode === 'whatsapp'" class="px-3 pb-3 pl-10">
          <label for="whatsappLink" class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('categories.whatsappLink') }} <span class="text-red-600">{{ t('common.required') }}</span>
          </label>
          <input
            v-model="whatsappLink"
            type="url"
            id="whatsappLink"
            :placeholder="t('categories.whatsappLinkPlaceholder')"
            :class="[
              'w-full px-3 py-2 text-sm border-2 rounded focus:outline-none bg-white',
              props.linkError ? 'border-red-500 focus:border-red-600' : 'border-gray-300 focus:border-blue-600'
            ]"
          >
          <p v-if="props.linkError" class="mt-1 text-xs text-red-600">{{ props.linkError }}</p>
        </div>

        <p
          v-if="option.value === 'none' && mode === 'none' && props.initialMode === 'app'"
          class="mx-3 mb-3 ml-10 p-2 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded"
        >
          {{ t('categories.conversationModeNoneWarning') }}
        </p>
      </div>
    </div>
  </fieldset>
</template>
